import { NOTE_ORDER, parseChordSymbol } from "@/music/chords/chordParser";

const SHARP_TO_FLAT = {
  "C#": "Db",
  "D#": "Eb",
  "F#": "Gb",
  "G#": "Ab",
  "A#": "Bb",
};

const getRootToken = (symbol = "") => String(symbol).trim().match(/^([A-G](?:#|b)?)/i)?.[1] || "";

const prefersFlats = (symbol) => /b/.test(getRootToken(symbol));

const getBaseQuality = (quality = "") => {
  const value = String(quality).trim().toLowerCase().replace(/^min/, "m").replace(/^-/, "m");
  if (value.includes("dim")) return "diminished";
  if (value.includes("aug") || value.includes("+")) return "augmented";
  if (value.includes("sus")) return "suspended";
  if (value.startsWith("m") && !value.startsWith("maj")) return "minor";
  return "major";
};

const getExtension = (quality = "") => {
  const value = String(quality).toLowerCase();
  if (value.includes("maj7") || value.includes("ma7")) return "maj7";
  if (value.startsWith("m") && !value.startsWith("maj") && value.includes("7")) return "m7";
  if (value.includes("7")) return "7";
  if (value.includes("sus2")) return "sus2";
  if (value.includes("sus4") || value === "sus") return "sus4";
  if (value.includes("dim")) return "dim";
  if (value.includes("aug") || value.includes("+")) return "aug";
  return "";
};

export const displayNote = (pitchClass, flatPreference = false) => {
  const note = flatPreference ? SHARP_TO_FLAT[pitchClass] || pitchClass : pitchClass;
  return String(note || "").replace(/b/g, "\u266d");
};

export const displayChordSymbol = (symbol) =>
  String(symbol || "").replace(/([A-G])b/g, "$1\u266d");

export const getChordDetails = (symbol) => {
  const parsed = parseChordSymbol(symbol);
  const useFlats = prefersFlats(symbol);
  const baseQuality = getBaseQuality(parsed.quality);
  const pitchClasses = parsed.pitchClasses || [];

  return {
    ...parsed,
    displayName: displayChordSymbol(symbol),
    displayRoot: displayNote(parsed.root, useFlats),
    displayBass: displayNote(parsed.bass, useFlats),
    displayNotes: pitchClasses.map((note) => displayNote(note, useFlats)),
    baseQuality,
    extension: getExtension(parsed.quality),
    prefersFlats: useFlats,
  };
};

export const getBassTriadDetails = (symbol) => {
  const details = getChordDetails(symbol);
  if (!details.recognized || !details.root) {
    return { ...details, triad: [], displayTriad: [], educational: false, intervalLabels: [] };
  }
  if (!["major", "minor"].includes(details.baseQuality)) {
    return {
      ...details,
      triad: [details.root],
      displayTriad: [details.displayRoot],
      educational: false,
      intervalLabels: ["R"],
    };
  }
  const rootIndex = NOTE_ORDER.indexOf(details.root);
  const intervals = details.baseQuality === "minor" ? [0, 3, 7] : [0, 4, 7];
  const triad = intervals.map((interval) => NOTE_ORDER[(rootIndex + interval) % NOTE_ORDER.length]);
  return {
    ...details,
    triad,
    displayTriad: triad.map((note) => displayNote(note, details.prefersFlats)),
    educational: true,
    intervalLabels: details.baseQuality === "minor" ? ["R", "3m", "5"] : ["R", "3", "5"],
  };
};

export const transposeChordSymbol = (symbol, semitones = 0) => {
  const raw = String(symbol || "").trim();
  const match = raw.match(/^([A-G](?:#|b)?)([^/]*)(?:\/([A-G](?:#|b)?))?$/i);
  const steps = Number(semitones);
  if (!match || !Number.isFinite(steps) || steps === 0) return raw;
  const parsed = parseChordSymbol(raw);
  if (!parsed.recognized || !parsed.root) return raw;
  const shift = (note) => NOTE_ORDER[(NOTE_ORDER.indexOf(note) + steps + NOTE_ORDER.length * 3) % NOTE_ORDER.length];
  const bass = parsed.bass ? `/${shift(parsed.bass)}` : "";
  return `${shift(parsed.root)}${match[2] || ""}${bass}`;
};
